import { RandomUtils } from "@dooboostore/core";
import { Slot } from "./Slot.js";
class NodeSlot {
  constructor(host, id) {
    if (!host) throw new Error("host node is required");
    if (!id) throw new Error("id is required");
    this.host = host;
    this.id = id;
    const markerData = NodeSlot.startEndMarkerData(id);
    this.startMarkerData = markerData.start;
    this.endMarkerData = markerData.end;
  }
  static startEndMarkerData(id, targetId) {
    const baseStart = `__slot_start__:${id}`;
    const baseEnd = `__slot_end__:${id}`;
    if (!targetId) {
      return { start: baseStart, end: baseEnd };
    }
    return { start: `${baseStart}:${targetId}`, end: `${baseEnd}:${targetId}` };
  }
  static slotIds(id, targetId) {
    const uid = RandomUtils.uuid();
    const markerData = NodeSlot.startEndMarkerData(id, targetId);
    return { start: `${markerData.start}:${uid}`, end: `${markerData.end}:${uid}` };
  }
  static slot(id, targetId) {
    const uid = RandomUtils.uuid();
    const markerData = NodeSlot.startEndMarkerData(id, targetId);
    const start = `<!--${markerData.start}:${uid}-->`;
    const end = `<!--${markerData.end}:${uid}-->`;
    return `${start}${end}`;
  }
  /**
   * Return HTML comment string pair for embedding into templates.
   * Example: host.innerHTML = `<div>...${nodeSlot}...</div>`
   */
  get slot() {
    return NodeSlot.slot(this.id);
  }
  /**
   * Return a slot marker pair for a specific targetId. Useful when the same
   * slot id must be targeted to different logical consumers. The produced
   * marker will look like: <!--__slot_start__:id:targetId:uid-->
   */
  slotFor(targetId) {
    return NodeSlot.slot(this.id, targetId);
  }
  // startCommentString and endCommentString getters removed — use slot() which
  // generates the full start/end comment pair including a unique uid per
  // insertion to ensure correct pairing when multiple slots with the same id
  // are inserted into the DOM.
  // toString removed intentionally to avoid accidental implicit string coercion.
  // Note: Symbol.toPrimitive removed to avoid custom primitive coercion logic.
  // valueOf and Symbol.toStringTag removed — prefer explicit use of slot getter/string conversion.
  findSlots(targetId) {
    const result = [];
    const doc = this.host.ownerDocument || (typeof document !== "undefined" ? document : void 0);
    const win = doc?.defaultView;
    if (!doc || !win) return result;
    const find = (target) => {
      const walker = doc.createTreeWalker(target, win.NodeFilter.SHOW_COMMENT, null, false);
      let node = walker.nextNode();
      const markerData = targetId ? NodeSlot.startEndMarkerData(this.id, targetId) : { start: this.startMarkerData, end: this.endMarkerData };
      const startPrefix = markerData.start + ":";
      while (node) {
        const data = node.data;
        if (data && data.indexOf(startPrefix) === 0) {
          const uid = data.substring(startPrefix.length);
          const expectedEnd = `${markerData.end}:${uid}`;
          let sibling = node.nextSibling;
          let foundEnd = null;
          while (sibling) {
            if (sibling.nodeType === win.Node.COMMENT_NODE && sibling.data === expectedEnd && sibling.parentNode === node.parentNode) {
              foundEnd = sibling;
              break;
            }
            sibling = sibling.nextSibling;
          }
          result.push(new Slot(node, foundEnd));
        }
        node = walker.nextNode();
      }
    };
    if (this.host.shadowRoot) {
      find(this.host.shadowRoot);
    }
    find(this.host);
    return result;
  }
  /**
   * Return an array of node arrays — for each found slot pair (via findSlots())
   * returns the nodes between its start and end (exclusive). Uses sibling
   * traversal (nextSibling) because findSlots pairs only same-parent markers.
   */
  nodes() {
    const slots = this.findSlots();
    return slots.map((s) => s.nodes());
  }
  /**
   * Shared disambiguation for the `(...items)` / `(targetId, ...items)` overload pair.
   * Items are always Node instances (or strings meant as literal content) coming from
   * callers; a leading string is therefore always treated as `targetId`, even when no
   * items follow — this avoids misclassifying `targetId` itself as content when the
   * caller has zero items to insert (e.g. a decorated method returning undefined).
   */
  static parseTargetArgs(args) {
    if (typeof args[0] === "string") {
      return { targetId: args[0], items: args.slice(1) };
    }
    return { targetId: void 0, items: args };
  }
  append(...args) {
    const { targetId, items } = NodeSlot.parseTargetArgs(args);
    if (!items || items.length === 0) return;
    const slots = this.findSlots(targetId);
    if (!slots || slots.length === 0) return;
    for (const s of slots) {
      s.append(...items);
    }
  }
  appendHtml(...args) {
    const { targetId, items } = NodeSlot.parseTargetArgs(args);
    const slots = this.findSlots(targetId);
    for (const s of slots) s.appendHtml(...items);
  }
  appendText(...args) {
    const { targetId, items } = NodeSlot.parseTargetArgs(args);
    const slots = this.findSlots(targetId);
    for (const s of slots) s.appendText(...items);
  }
  appendCopy(...args) {
    const { targetId, items } = NodeSlot.parseTargetArgs(args);
    if (!items || items.length === 0) return;
    const slots = this.findSlots(targetId);
    if (!slots || slots.length === 0) return;
    for (const s of slots) {
      s.appendCopy(...items);
    }
  }
  prepend(...args) {
    const { targetId, items } = NodeSlot.parseTargetArgs(args);
    if (!items || items.length === 0) return;
    const slots = this.findSlots(targetId);
    if (!slots || slots.length === 0) return;
    for (const s of slots) {
      s.prepend(...items);
    }
  }
  prependHtml(...args) {
    const { targetId, items } = NodeSlot.parseTargetArgs(args);
    const slots = this.findSlots(targetId);
    for (const s of slots) s.prependHtml(...items);
  }
  prependText(...args) {
    const { targetId, items } = NodeSlot.parseTargetArgs(args);
    const slots = this.findSlots(targetId);
    for (const s of slots) s.prependText(...items);
  }
  prependCopy(...args) {
    const { targetId, items } = NodeSlot.parseTargetArgs(args);
    if (!items || items.length === 0) return;
    const slots = this.findSlots(targetId);
    if (!slots || slots.length === 0) return;
    for (const s of slots) {
      s.prependCopy(...items);
    }
  }
  /**
   * Clear contents of slots. If targetId is provided, only clears slots that
   * were created with that targetId segment. Otherwise clears all slots for
   * this id.
   */
  clear(targetId) {
    const slots = this.findSlots(targetId);
    for (const s of slots) {
      s.clear();
    }
  }
  replaceChildren(...args) {
    const { targetId, items } = NodeSlot.parseTargetArgs(args);
    const slots = this.findSlots(targetId);
    if (!slots || slots.length === 0) return;
    for (const s of slots) {
      s.replaceChildren(...items);
    }
  }
  replaceChildrenHtml(...args) {
    const { targetId, items } = NodeSlot.parseTargetArgs(args);
    const slots = this.findSlots(targetId);
    if (!slots || slots.length === 0) return;
    for (const s of slots) {
      s.replaceChildrenHtml(...items);
    }
  }
  replaceChildrenText(...args) {
    const { targetId, items } = NodeSlot.parseTargetArgs(args);
    const slots = this.findSlots(targetId);
    if (!slots || slots.length === 0) return;
    for (const s of slots) {
      s.replaceChildrenText(...items);
    }
  }
  findAll(...args) {
    let targetId;
    let predicate;
    if (typeof args[0] === "string") {
      targetId = args[0];
      predicate = args[1];
    } else {
      predicate = args[0];
    }
    const groups = this.nodesForTarget(targetId);
    const flat = groups.reduce((acc, cur) => acc.concat(cur), []);
    if (!predicate) return flat;
    return flat.filter(predicate);
  }
  querySelector(...args) {
    let targetId;
    let selector;
    if (args.length === 1) {
      selector = args[0];
    } else {
      targetId = args[0];
      selector = args[1];
    }
    if (!selector) return null;
    const groups = this.nodesForTarget(targetId);
    for (const nodes of groups) {
      for (const n of nodes) {
        if (n.nodeType === Node.ELEMENT_NODE) {
          const el = n;
          if (el.matches && el.matches(selector)) return el;
          const found = el.querySelector(selector);
          if (found) return found;
        }
      }
    }
    return null;
  }
  querySelectorAll(...args) {
    let targetId;
    let selector;
    if (args.length === 1) {
      selector = args[0];
    } else {
      targetId = args[0];
      selector = args[1];
    }
    if (!selector) return [];
    const out = [];
    const groups = this.nodesForTarget(targetId);
    for (const nodes of groups) {
      for (const n of nodes) {
        if (n.nodeType === Node.ELEMENT_NODE) {
          const el = n;
          if (el.matches && el.matches(selector)) out.push(el);
          const found = el.querySelectorAll(selector);
          if (found && found.length) out.push(...Array.from(found));
        }
      }
    }
    return out;
  }
  // helper to get nodes for a specific targetId or all nodes when targetId undefined
  nodesForTarget(targetId) {
    if (targetId) {
      const slots = this.findSlots(targetId);
      return slots.map((s) => s.nodes());
    }
    return this.nodes();
  }
}
export {
  NodeSlot
};
//# sourceMappingURL=NodeSlot.js.map
